/*
 * A five-page router, deliberately kept to the smallest thing that works.
 *
 * The site is a marketing page plus a few standalone pages, so a routing
 * library would be more machinery than the problem needs. Links stay real
 * anchors with real hrefs — they are crawlable, they work on middle-click, and
 * they still resolve if the bundle never runs. `PageLink` only upgrades a plain
 * left-click into a history push so the transition feels instant.
 *
 * Production hosting must serve index.html for these paths. See README.
 */

import { useEffect, useState } from 'react';

export const ROUTES = {
  home: '/',
  privacy: '/privacy',
  deleteAccount: '/delete-account',
  android: '/android',
  teamGuide: '/help/team',
};

const NAVIGATE_EVENT = 'tl:navigate';

/** Trailing slashes and casing are the same destination to a reader. */
export const normalisePath = (pathname) => {
  if (!pathname) return ROUTES.home;
  const trimmed = pathname.replace(/\/+$/, '').toLowerCase();
  return trimmed === '' ? ROUTES.home : trimmed;
};

export const navigate = (to) => {
  if (normalisePath(window.location.pathname) !== normalisePath(to)) {
    window.history.pushState({}, '', to);
    window.dispatchEvent(new Event(NAVIGATE_EVENT));
  }
  window.scrollTo(0, 0);
};

export const useRoute = () => {
  const [route, setRoute] = useState(() => normalisePath(window.location.pathname));

  useEffect(() => {
    const sync = () => setRoute(normalisePath(window.location.pathname));

    // A push that happened between render and this effect must not be missed.
    sync();
    window.addEventListener('popstate', sync);
    window.addEventListener(NAVIGATE_EVENT, sync);

    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAVIGATE_EVENT, sync);
    };
  }, []);

  return route;
};

/**
 * Section anchors only exist on the homepage. From a legal route the same link
 * has to be a real navigation back to the homepage, hash included — a bare
 * `#product` there points at nothing. The browser handles the load and the
 * scroll, so the router stays out of it.
 */
export const sectionHref = (href, route) => {
  if (!href.startsWith('#')) return href;
  return route === ROUTES.home ? href : `${ROUTES.home}${href}`;
};

/** Modified clicks (new tab, download, middle-click) belong to the browser. */
const isPlainClick = (event) =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

export const PageLink = ({ to, onClick, children, ...props }) => {
  const handleClick = (event) => {
    if (onClick) onClick(event);
    if (!isPlainClick(event)) return;
    event.preventDefault();
    navigate(to);
  };

  return (
    <a {...props} href={to} onClick={handleClick}>
      {children}
    </a>
  );
};
